const MAX_STEERING_Q = 32_767

export class InputManager {
  private readonly canvas: HTMLCanvasElement
  private readonly pressedKeys = new Set<string>()
  private activePointerId: number | null = null
  private pointerStartX = 0
  private pointerSteering = 0
  private dragSteering = false

  public constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    window.addEventListener('keydown', this.handleKeyDown, { passive: false })
    window.addEventListener('keyup', this.handleKeyUp)
    canvas.addEventListener('pointerdown', this.handlePointerDown, { passive: false })
    canvas.addEventListener('pointermove', this.handlePointerMove, { passive: false })
    canvas.addEventListener('pointerup', this.handlePointerEnd)
    canvas.addEventListener('pointercancel', this.handlePointerEnd)
    canvas.addEventListener('contextmenu', this.preventDefault)
  }

  public get steeringQ(): number {
    const keyboard = (this.isRightPressed() ? 1 : 0) - (this.isLeftPressed() ? 1 : 0)
    const steering = keyboard !== 0 ? keyboard : this.pointerSteering
    return Math.round(Math.max(-1, Math.min(1, steering)) * MAX_STEERING_Q)
  }

  public setDragSteering(enabled: boolean): void {
    this.dragSteering = enabled
    this.reset()
  }

  public reset(): void {
    this.pressedKeys.clear()
    this.pointerSteering = 0
    if (this.activePointerId !== null && this.canvas.hasPointerCapture(this.activePointerId)) {
      this.canvas.releasePointerCapture(this.activePointerId)
    }
    this.activePointerId = null
  }

  public dispose(): void {
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('keyup', this.handleKeyUp)
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown)
    this.canvas.removeEventListener('pointermove', this.handlePointerMove)
    this.canvas.removeEventListener('pointerup', this.handlePointerEnd)
    this.canvas.removeEventListener('pointercancel', this.handlePointerEnd)
    this.canvas.removeEventListener('contextmenu', this.preventDefault)
  }

  private isLeftPressed(): boolean {
    return this.pressedKeys.has('ArrowLeft') || this.pressedKeys.has('KeyA')
  }

  private isRightPressed(): boolean {
    return this.pressedKeys.has('ArrowRight') || this.pressedKeys.has('KeyD')
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (['ArrowLeft', 'ArrowRight', 'KeyA', 'KeyD'].includes(event.code)) {
      event.preventDefault()
      this.pressedKeys.add(event.code)
    }
  }

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    this.pressedKeys.delete(event.code)
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (this.activePointerId !== null) return
    event.preventDefault()
    this.activePointerId = event.pointerId
    this.pointerStartX = event.clientX
    this.canvas.setPointerCapture(event.pointerId)
    this.pointerSteering = event.clientX < window.innerWidth * 0.5 ? -1 : 1
  }

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return
    event.preventDefault()
    if (this.dragSteering) {
      this.pointerSteering = Math.max(-1, Math.min(1, (event.clientX - this.pointerStartX) / 90))
    } else {
      this.pointerSteering = event.clientX < window.innerWidth * 0.5 ? -1 : 1
    }
  }

  private readonly handlePointerEnd = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return
    this.pointerSteering = 0
    this.activePointerId = null
  }

  private readonly preventDefault = (event: Event): void => event.preventDefault()
}
